import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
  TrackingService,
  TrackingOverview,
  TrackingOverviewRow,
  TrackingSeverity,
  TransitRule,
} from '../api';

// ============================================================================
// Tracking — a support kolléga áttekintője: a kiküldött csomagok egy helyen,
// futárral, állapottal, színezett súlyossággal és várható érkezéssel.
// Az adat forrása az AfterShip (tartalék: ShippyPro), a várható érkezést a
// feladás napja + célországonkénti szállítási idő adja (itt szerkeszthető).
// ============================================================================

type RangeTab = '7 nap' | '30 nap' | '2 hónap' | 'Egyedi';
type StatusFilter = 'all' | TrackingSeverity | 'delivered';

const COUNTRY_NAMES: Record<string, string> = {
  DE: 'Németország', GB: 'Egyesült Királyság', US: 'Egyesült Államok',
  FR: 'Franciaország', AT: 'Ausztria', NL: 'Hollandia', BE: 'Belgium',
  IT: 'Olaszország', ES: 'Spanyolország', PT: 'Portugália', PL: 'Lengyelország',
  CZ: 'Csehország', SK: 'Szlovákia', HU: 'Magyarország', RO: 'Románia',
  DK: 'Dánia', SE: 'Svédország', FI: 'Finnország', NO: 'Norvégia',
  IE: 'Írország', CH: 'Svájc', LU: 'Luxemburg', SI: 'Szlovénia',
  HR: 'Horvátország', BG: 'Bulgária', GR: 'Görögország', EE: 'Észtország',
  LV: 'Lettország', LT: 'Litvánia', CA: 'Kanada', AU: 'Ausztrália',
  JP: 'Japán', MT: 'Málta', CY: 'Ciprus', NZ: 'Új-Zéland', IL: 'Izrael',
};

interface RowVm {
  row: TrackingOverviewRow;
  countryLabel: string;
  orderedLabel: string;
  shippedLabel: string | null;
  transitLabel: string | null;
  lastEventTime: string | null;
  estimateLabel: string | null;
  estimateNote: string | null;
}

@Component({
  selector: 'app-tracking',
  templateUrl: './tracking.component.html',
  styleUrl: './tracking.component.scss'
})
export class TrackingComponent implements OnInit {
  readonly rangeTabs: RangeTab[] = ['7 nap', '30 nap', '2 hónap', 'Egyedi'];
  range: RangeTab = '30 nap';
  customFrom = '';
  customTo = '';

  q = '';
  private searchTimer: any = null;

  statusFilter: StatusFilter = 'all';
  carrier = 'Mind';
  carrierOptions: string[] = ['Mind'];

  loading = false;
  loadFailed = false;
  overview: TrackingOverview | null = null;
  rows: RowVm[] = [];
  visible: RowVm[] = [];
  rangeLabel = '';

  // Szállítási idők szerkesztője
  settingsOpen = false;
  rules: TransitRule[] = [];
  rulesSaving = false;
  rulesError: string | null = null;
  rulesSavedAt: Date | null = null;

  // Összes nyitott küldemény frissítése most
  refreshingAll = false;
  refreshAllResult: string | null = null;

  // Etsy kézbesítési idők visszatöltése a meglévő rendelésekre
  backfilling = false;
  backfillResult: string | null = null;

  constructor(private tracking: TrackingService, private router: Router) {}

  ngOnInit(): void {
    const today = new Date();
    const from = new Date(today);
    from.setDate(from.getDate() - 29);
    this.customFrom = this.isoDate(from);
    this.customTo = this.isoDate(today);
    this.load();
  }

  // ------------------------------------------------------------- szűrők

  get isCustom(): boolean { return this.range === 'Egyedi'; }

  setRange(tab: RangeTab): void {
    if (this.range === tab) { return; }
    this.range = tab;
    if (!this.isCustom) {
      this.load();
    }
  }

  onCustomDateChange(): void {
    if (this.customFrom && this.customTo) {
      this.load();
    }
  }

  onSearchChange(): void {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.load(), 350);
  }

  setStatus(filter: StatusFilter): void {
    this.statusFilter = this.statusFilter === filter && filter !== 'all' ? 'all' : filter;
    this.applyFilters();
  }

  onCarrierChange(): void {
    this.applyFilters();
  }

  private period(): { from: string; to: string } {
    const today = new Date();
    if (this.isCustom) {
      return { from: this.customFrom, to: this.customTo };
    }
    const days = this.range === '7 nap' ? 7 : this.range === '30 nap' ? 30 : 60;
    const from = new Date(today);
    from.setDate(from.getDate() - (days - 1));
    return { from: this.isoDate(from), to: this.isoDate(today) };
  }

  // ------------------------------------------------------------ betöltés

  load(): void {
    const { from, to } = this.period();
    this.loading = true;
    this.loadFailed = false;
    this.rangeLabel = `${this.fmtDate(from)} – ${this.fmtDate(to)}`;
    this.tracking.getOverview(from, to, this.q).subscribe({
      next: (overview) => {
        this.overview = overview;
        this.rows = (overview?.rows || []).map((row) => this.toVm(row));
        this.rules = (overview?.transitRules || []).map((rule) => ({ ...rule }));
        const carriers = new Set<string>();
        this.rows.forEach((vm) => { if (vm.row.carrierName) { carriers.add(vm.row.carrierName); } });
        this.carrierOptions = ['Mind', ...[...carriers].sort()];
        if (!this.carrierOptions.includes(this.carrier)) {
          this.carrier = 'Mind';
        }
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
        this.rows = [];
        this.visible = [];
      },
    });
  }

  private applyFilters(): void {
    this.visible = this.rows.filter((vm) => {
      const row = vm.row;
      if (this.carrier !== 'Mind' && row.carrierName !== this.carrier) {
        return false;
      }
      switch (this.statusFilter) {
        case 'delivered': return !!row.deliveredAt || row.statusCode === 6000;
        case 'green': return row.severity === 'green' && !row.deliveredAt && row.statusCode !== 6000;
        case 'yellow': return row.severity === 'yellow';
        case 'red': return row.severity === 'red';
        default: return true;
      }
    });
  }

  get summary() {
    return this.overview?.summary || { total: 0, green: 0, yellow: 0, red: 0, delivered: 0 };
  }

  /** „Rendben" = zöld, de még nem kézbesített (a kézbesítettek külön számolnak). */
  get inTransitOk(): number {
    return this.rows.filter((vm) => vm.row.severity === 'green' && !vm.row.deliveredAt && vm.row.statusCode !== 6000).length;
  }

  openOrder(row: TrackingOverviewRow): void {
    this.router.navigate(['/orders', row.orderId]);
  }

  trackByCode(_: number, vm: RowVm): string {
    return `${vm.row.orderId}-${vm.row.trackingCode}`;
  }

  severityClass(severity: TrackingSeverity): string {
    return `tro-sev--${severity}`;
  }

  pillClass(severity: TrackingSeverity): string {
    return `stk-pill--${severity}`;
  }

  // ------------------------------------------------------ szállítási idők

  toggleSettings(): void {
    this.settingsOpen = !this.settingsOpen;
    this.rulesError = null;
  }

  addRule(): void {
    this.rules = [...this.rules, { scope: '', min_days: 5, max_days: 10 }];
  }

  removeRule(index: number): void {
    this.rules = this.rules.filter((_, i) => i !== index);
  }

  scopeLabel(scope: string): string {
    const key = `${scope || ''}`.toUpperCase();
    if (key === '*') { return 'minden más ország'; }
    if (key === 'EU') { return 'EU-tagállamok'; }
    return COUNTRY_NAMES[key] || '';
  }

  saveRules(): void {
    if (this.rulesSaving) { return; }
    this.rulesSaving = true;
    this.rulesError = null;
    const items = this.rules.map((rule) => ({
      scope: `${rule.scope || ''}`.trim().toUpperCase(),
      min_days: Number(rule.min_days),
      max_days: Number(rule.max_days),
    }));
    this.tracking.saveTransitRules(items).subscribe({
      next: (saved) => {
        this.rules = saved.map((rule) => ({ ...rule }));
        this.rulesSaving = false;
        this.rulesSavedAt = new Date();
        // Az új idők a várható érkezést és a késést is átírják — újratöltünk.
        this.load();
      },
      error: (err) => {
        this.rulesSaving = false;
        this.rulesError = err?.error?.message || 'A mentés nem sikerült.';
      },
    });
  }

  /** Minden nyitott csomag lekérdezése a futártól most — eltarthat pár percig. */
  refreshAll(): void {
    if (this.refreshingAll) { return; }
    this.refreshingAll = true;
    this.refreshAllResult = null;
    this.tracking.refreshOpen(300).subscribe({
      next: (result) => {
        this.refreshingAll = false;
        this.refreshAllResult = `${result.refreshed} küldemény frissítve${result.failed ? `, ${result.failed} hibával` : ''}.`;
        this.load();
      },
      error: (err) => {
        this.refreshingAll = false;
        this.refreshAllResult = err?.error?.message || 'A frissítés nem sikerült.';
      },
    });
  }

  /**
   * A már meglévő rendelésekre az Etsy szállítási profil szerinti várható
   * érkezés visszatöltése. Rendelésenként egy Etsy-hívás — eltarthat egy percig.
   */
  runBackfill(): void {
    if (this.backfilling) { return; }
    this.backfilling = true;
    this.backfillResult = null;
    this.tracking.backfillEtsyDeliveryEstimates(90).subscribe({
      next: (results) => {
        this.backfilling = false;
        const parts = (results || []).map((r) => `${r.shop}: ${r.filled} kitöltve / ${r.checked} vizsgált${r.failed ? `, ${r.failed} hiba` : ''}`);
        this.backfillResult = parts.length ? parts.join(' · ') : 'Nincs engedélyezett Etsy bolt.';
        this.load();
      },
      error: (err) => {
        this.backfilling = false;
        this.backfillResult = err?.error?.message || 'A visszatöltés nem sikerült.';
      },
    });
  }

  // ------------------------------------------------------------- nézet

  private toVm(row: TrackingOverviewRow): RowVm {
    const country = `${row.countryIso || ''}`.toUpperCase();
    const countryLabel = country ? (COUNTRY_NAMES[country] ? `${COUNTRY_NAMES[country]} (${country})` : country) : '';

    let transitLabel: string | null = null;
    if (row.shippedAt) {
      transitLabel = row.deliveredAt
        ? `${row.daysInTransit} nap alatt érkezett`
        : row.daysInTransit !== null ? `${row.daysInTransit} napja úton` : null;
    }

    let estimateLabel: string | null = null;
    let estimateNote: string | null = null;
    if (row.estimatedFrom && row.estimatedTo) {
      estimateLabel = `${this.fmtDate(row.estimatedFrom)} – ${this.fmtDate(row.estimatedTo)}`;
      estimateNote = row.estimateFromActualShipDate
        ? null
        : (row.shipByAt ? `a ${this.fmtDate(row.shipByAt)} feladási határidőből` : null);
    }

    return {
      row,
      countryLabel,
      orderedLabel: row.orderedAt ? this.fmtDate(row.orderedAt) : '',
      shippedLabel: row.shippedAt ? this.fmtDate(row.shippedAt) : null,
      transitLabel,
      lastEventTime: row.lastEventAt ? this.fmtDateTime(row.lastEventAt) : null,
      estimateLabel,
      estimateNote,
    };
  }

  private isoDate(date: Date): string {
    const y = date.getFullYear();
    const m = `${date.getMonth() + 1}`.padStart(2, '0');
    const d = `${date.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  fmtDate(iso: string): string {
    const date = new Date(iso.length === 10 ? `${iso}T00:00:00` : iso);
    return Number.isNaN(date.getTime())
      ? ''
      : date.toLocaleDateString('hu-HU', { month: 'short', day: 'numeric' });
  }

  private fmtDateTime(iso: string): string {
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) { return ''; }
    const time = date.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit' });
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
    const dayDiff = Math.round((startOfDay(new Date()) - startOfDay(date)) / 86400000);
    if (dayDiff === 0) { return `ma ${time}`; }
    if (dayDiff === 1) { return `tegnap ${time}`; }
    return `${this.fmtDate(iso)} ${time}`;
  }
}
